import { PasskeyService } from '../../../services/passkey/passkey.service'
import { DatePipe } from '@angular/common'
import { Component, ChangeDetectionStrategy, OnInit } from '@angular/core'
import { Auth } from '@angular/fire/auth'
import { MatSnackBar } from '@angular/material/snack-bar'
import { Title } from '@angular/platform-browser'
import { Router } from '@angular/router'
import { lastValueFrom } from 'rxjs'

import { LoadingComponent } from '../../../components/loading/loading.component'
import { MfaMethodType } from '../../../../_generated/graphql'
import { ButtonComponent } from '../../../components/button/button.component'
import {
  DialogResult,
  MessageDialogService,
} from '../../../components/message-dialog/message-dialog.service'
import { AuthService, MFA_SETTINGS_PATH } from '../../../services/auth.service'
import { MfaStatus, UserService } from '../../../services/user/user.service'
import { getTitle } from '../../../util/accessibility'

@Component({
  selector: 'app-mfa-settings-page',
  standalone: true,
  imports: [DatePipe, ButtonComponent, LoadingComponent],
  templateUrl: './mfa-settings-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './mfa-settings-page.component.scss',
})
export class MfaSettingsPageComponent implements OnInit {
  status: MfaStatus | null = null
  busy = false
  loadFailed = false
  constructor(
    private auth: Auth,
    private authService: AuthService,
    private users: UserService,
    private passkeys: PasskeyService,
    private router: Router,
    private dialog: MessageDialogService,
    private snackBar: MatSnackBar,
    title: Title,
  ) {
    title.setTitle(getTitle('セキュリティ設定'))
  }

  async ngOnInit(): Promise<void> {
    try {
      const intent = this.authService.consumePendingIntent()
      if (!(await this.refresh())) return
      if (intent?.type === 'add-passkey') await this.addPasskey()
      if (intent?.type === 'remove-passkey') await this.remove(intent.id)
      if (intent?.type === 'add-sms') await this.addSms()
      if (intent?.type === 'add-totp') await this.addTotp()
      if (intent?.type === 'remove') await this.remove(intent.factorId)
    } catch {
      this.message('セキュリティ設定の取得に失敗しました。再度お試しください。')
    }
  }

  get canAddSms(): boolean {
    return this.status?.enrollableMethods.includes(MfaMethodType.Sms) === true
  }
  get canAddTotp(): boolean {
    return this.status?.enrollableMethods.includes(MfaMethodType.Totp) === true
  }
  get canAddPasskey(): boolean {
    return (
      this.status?.enrollableMethods.includes(MfaMethodType.Passkey) === true
    )
  }
  async addPasskey(): Promise<void> {
    if (!this.canAddPasskey) return
    await this.addMethod('add-passkey')
  }
  get hasPasskey(): boolean {
    return (
      this.status?.methods.some(
        (method) => method.type === MfaMethodType.Passkey,
      ) === true
    )
  }
  get hasFirebaseMethod(): boolean {
    return (
      this.status?.methods.some(
        (method) =>
          method.type === MfaMethodType.Sms ||
          method.type === MfaMethodType.Totp,
      ) === true
    )
  }
  cannotRemove(): boolean {
    return (
      this.status?.requiredForUser === true && this.status.methods.length <= 1
    )
  }

  async addSms(): Promise<void> {
    if (!this.canAddSms) return
    await this.addMethod('add-sms')
  }

  async addTotp(): Promise<void> {
    if (!this.canAddTotp) return
    await this.addMethod('add-totp')
  }

  private async addMethod(
    type: 'add-sms' | 'add-totp' | 'add-passkey',
  ): Promise<void> {
    if (this.busy) return
    const user = this.auth.currentUser
    if (!user) {
      await this.router.navigate(['/login'])
      return
    }
    this.busy = true
    try {
      await this.authService.reloadCurrentUser()
      if (type !== 'add-passkey' && !user.emailVerified) {
        try {
          await this.authService.sendEmailVerification()
          this.message(
            'メールアドレスの確認が必要です。確認メールを送りました。メール内のリンクを開いてから、もう一度お試しください',
          )
        } catch {
          this.message('メールの送信に失敗しました。再試行してください。')
        }
        return
      }
      const authTime = await this.authService.getAuthTime()
      if (Date.now() - new Date(authTime).getTime() > 5 * 60 * 1000) {
        await this.authService.startReauthentication({ type })
        return
      }
      this.authService.setReturnUrl(MFA_SETTINGS_PATH)
      this.authService.markMfaEnrollmentFromSettings()
      await this.router.navigate([
        type === 'add-passkey'
          ? '/auth/passkey-setup'
          : type === 'add-sms'
            ? '/auth/phone-setup'
            : '/auth/totp-setup',
      ])
    } catch {
      this.message('操作に失敗しました。再度お試しください。')
    } finally {
      this.busy = false
    }
  }

  async remove(factorId: string): Promise<void> {
    const method = this.status?.methods.find((item) => item.id === factorId)
    if (this.busy || !method || this.cannotRemove()) return
    const user = this.auth.currentUser
    if (!user) {
      await this.router.navigate(['/login'])
      return
    }
    this.busy = true
    try {
      const result = await lastValueFrom(
        this.dialog.showConfirm(
          method.type === MfaMethodType.Passkey
            ? 'このパスキーを削除しますか？全端末でログアウトされます。'
            : 'この認証方法を削除しますか？',
        ),
      )
      if (result !== DialogResult.PrimaryButtonClicked) return
      if (method.type === MfaMethodType.Passkey) {
        await lastValueFrom(this.passkeys.deletePasskey(factorId))
        await this.authService.logout()
        this.authService.redirectToLogin({ info: 'passkey_removed' })
        return
      }
      await this.authService.unenrollFactor(factorId)
      this.message('削除しました')
      await this.refresh()
    } catch (error) {
      const code = (error as { code?: string }).code
      if (code === 'PASSKEY_NOT_ALLOWED') {
        this.message('必須のため、最後の認証手段は削除できません')
      } else if (code === 'RECENT_LOGIN_REQUIRED') {
        await this.authService.startReauthentication({
          type: 'remove-passkey',
          id: factorId,
        })
      } else if (code === 'auth/requires-recent-login') {
        await this.authService.startReauthentication({
          type: 'remove',
          factorId,
        })
      } else if (code === 'auth/user-token-expired') {
        await this.authService.signOutPreservingReturnUrl()
        this.authService.redirectToLogin({ info: 'mfa_removed' })
      } else {
        this.message('削除に失敗しました。再度お試しください。')
      }
    } finally {
      this.busy = false
    }
  }

  // 一度で complete する await に untilDestroyed を混ぜると破棄時に EmptyError になるため不要。
  async refresh(): Promise<boolean> {
    this.loadFailed = false
    try {
      this.status = await lastValueFrom(this.users.getMfaStatus())
      return true
    } catch {
      this.loadFailed = true
      this.message('セキュリティ設定の取得に失敗しました。再度お試しください。')
      return false
    }
  }
  private message(text: string): void {
    this.snackBar.open(text, '閉じる', { duration: 8000 })
  }
}
